<template>
	<Layout>
		<div class="container">
			<div class="integration-top">
				<div class="title">Integration</div>
				<div class="actions">
					<div
						@click="state.ssoModal = true"
						v-if="
							state.integration === 'synergy' ||
							state.integration === 'finalsite' ||
							(['blackbaud', 'blackbaud_new'].includes(
								schoolStore.schoolDetails.data?.integration || ''
							) &&
								schoolStore.schoolDetails.data?.integration === state.integration)
						"
						class="action"
						data-cy="sso-button"
					>
						<v-icon class="icon">mdi-security</v-icon>
						<span class="text">SSO</span>
					</div>
					<div class="action" @click="state.instructionsModal = true" data-cy="instructions-button">
						<span class="material-icons">integration_instructions</span>
						<span class="text">INSTRUCTIONS</span>
					</div>
				</div>
			</div>

			<div
				class="integration-body"
				data-cy="integration-body"
				:data-loading="loadingIntegration || undefined"
			>
				<div class="d-flex align-center mt-2 gap-4 flex-wrap w-100">
					<div style="max-width: 100%">
						<div class="title">Integration Type</div>
						<div class="d-flex align-center mt-2">
							<v-select
								density="comfortable"
								color="primary"
								bg-color="white"
								:items="selectableIntegrationTypes"
								v-model="state.integration"
								style="width: 300px"
								class="custom-input"
								item-title="title"
								item-value="value"
								:disabled="state.loadingSave"
								data-cy="integration-type-select"
								:menu-props="{
                  ['data-cy' as string]: 'integration-type-select-menu'
                }"
							></v-select>
						</div>
					</div>
				</div>

				<div class="d-flex align-center mt-1 gap-4 flex-wrap w-100 mb-2">
					<div style="max-width: 100%" v-if="state.integration === 'seniorsystems'">
						<div class="title">Endpoint URL</div>
						<div class="d-flex align-center mt-2 gap-4">
							<v-text-field
								density="comfortable"
								color="primary"
								bg-color="white"
								v-model="state.endpoint_url"
								style="width: 300px"
								class="custom-input"
								:loading="loadingIntegration"
								data-cy="integration-endpoint"
							></v-text-field>
						</div>
					</div>
					<div style="max-width: 100%" v-if="state.integration === 'sungard'">
						<div class="title">Private Key</div>
						<div class="d-flex align-center mt-2 gap-4">
							<v-text-field
								density="comfortable"
								color="primary"
								bg-color="white"
								v-model="state.private_key"
								style="width: 300px"
								class="custom-input"
								:loading="loadingIntegration"
								data-cy="integration-private-key"
							></v-text-field>
						</div>
					</div>
					<template v-if="state.integration === 'infinitecampus'">
						<div style="max-width: 100%">
							<div class="title">Login URL</div>
							<div class="d-flex align-center mt-2 gap-4">
								<v-text-field
									density="comfortable"
									color="primary"
									bg-color="white"
									v-model="state.login_url"
									style="width: 300px"
									class="custom-input"
									:loading="loadingIntegration"
									:data-loading="loadingIntegration"
									data-cy="integration-login-url"
								></v-text-field>
							</div>
						</div>
						<div style="max-width: 100%" data-cy="integration-appname-container">
							<div class="title">App Name</div>
							<div class="d-flex align-center mt-2 gap-4">
								<v-text-field
									density="comfortable"
									color="primary"
									bg-color="white"
									v-model="state.appname"
									style="width: 300px"
									class="custom-input"
									:loading="loadingIntegration"
									data-cy="integration-appname"
								></v-text-field>
							</div>
						</div>
						<div class="mt-2">
							<v-btn
								v-if="
									schoolStore.schoolDetails.data?.integration !== state.integration ||
									(showSave && !loadingIntegration)
								"
								@click="save"
								:loading="state.loadingSave"
								data-cy="infinitecampus-save-btn"
							>
								Save
							</v-btn>
						</div>
					</template>
					<template v-if="['infinitecampus', 'synergy'].includes(state.integration)">
						<div class="mt-4" style="width: 100%;">
							<v-tabs v-model="state.csvOnerosterTab">
								<v-tab
									value="oneroster"
									:data-cy="`${state.integration}-tab-oneroster`"
								>
									OneRoster
								</v-tab>
								<v-tab value="csv" :data-cy="`${state.integration}-tab-csv`">
									CSV
								</v-tab>
							</v-tabs>
							<csvIntegration
								v-if="state.csvOnerosterTab === 'csv'"
								:integration="state.integration"
								:data="integrationData"
								:loading-data="loadingIntegration"
							/>
							<OneRoster
								v-if="state.csvOnerosterTab === 'oneroster'"
								:integration_type="1.1"
								@reload:data="reloadIntegration()"
							/>
						</div>
					</template>
					<template v-if="state.integration == 'facts'">
						<FactsApi 
							:facts_subscription_key="state.facts_subscription_key" 
							:facts_api_key="state.facts_api_key" 
							:loadingIntegration="loadingIntegration"
							@reload:data="reloadIntegration()"
							@update:facts_api_key="state.facts_api_key = $event"
						/>
					</template>
					<template v-if="state.integration == 'nycdoe'">
						<NycDoe2
							:nycdoe_school_code="state.nycdoe_school_code"
							:loadingIntegration="loadingIntegration"
							@reload:data="reloadIntegration()"
							@update:nycdoe_school_code="state.nycdoe_school_code = $event"
						/>
					</template>
					<template v-if="state.integration == 'oneroster'">
						<OneRoster :integration_type="1.1" @reload:data="reloadIntegration()"/>
					</template>
				</div>
				<template v-if="state.integration == 'blackbaud_new'">
					<BlackbaudNew
						:data="integrationData"
						:loading-data="loadingIntegration"
						@update:data="
							(val) => {
								if (integrationData) Object.assign(integrationData, val);
							}
						"
						@reload:data="reloadIntegration()"
						v-model:sso-modal="state.ssoModal"
					/>
				</template>
				<!-- <div
					v-if="state.integration == 'oneroster'"
					class="mt-1 mb-1"
				>
					<OneRosterSchools
						:integration_type="state.integration === 'nycdoe' ? 'nycdoe' : '1.1'"
					/>
				</div> -->

				<template
					v-if="schoolStore.schoolDetails.data?.integration === state.integration"
				>
					<csvIntegration
						:integration="state.integration"
						v-if="
							[
								'none',
								'csv',
								'google',
								'sungard',
								
								'finalsite',
								'seniorsystems',
								'hybrid',
							].includes(state.integration)
						"
						:data="integrationData"
						:loading-data="loadingIntegration"
					></csvIntegration>
					<BlackBaud
						:data="integrationData"
						:loading-data="loadingIntegration"
						@update:data="Object.assign(integrationData, $event)"
						@reload:data="reloadIntegration()"
						v-if="state.integration === 'blackbaud'"
						v-model:sso-modal="state.ssoModal"
					></BlackBaud>
				</template>

				<div class="mt-4 mb-4" data-cy="integration-save-footer">
					<v-switch
						v-if="['csv', 'facts', 'google', 'oneroster'].includes(state.integration)"
						v-model="state.reset_password_required"
						color="secondary"
						@change="save()"
						label="Require Parents to Reset Password After First Login"
						data-cy="reset-password-switch"
					></v-switch>
					<v-btn
						v-if="
							state.integration !== 'infinitecampus' &&
							(schoolStore.schoolDetails.data?.integration !== state.integration ||
							(showSave && !loadingIntegration))
						"
						class="mt-2"
						@click="save"
						:loading="state.loadingSave"
						data-cy="integration-save-btn"
					>
						Save
					</v-btn>
				</div>

				<InstructionsModal
					:integration="integrationTypes.find(i=> i.value===state.integration)!"
					v-model:show="state.instructionsModal"
				></InstructionsModal>
				<SynergySsoModal
					v-if="state.integration === 'synergy'"
					:show="state.ssoModal"
					:loading="state.loadingSave"
					v-model:sso-enabled="state.synergySsoEnabled"
					v-model:pxp-url="state.synergyPxpUrl"
					v-model:application-name="state.synergyApplicationName"
					@update:show="state.ssoModal = $event"
					@save="save"
				/>
				<SideModal
					v-if="state.integration === 'finalsite'"
					:show="state.ssoModal"
					@update:show="state.ssoModal = $event"
					location="right"
					close-on-overlay-click
					width="640px"
					data-cy="sso-modal"
				>
					<div class="finalsite-sso-modal" data-cy="finalsite-sso-modal">
						<div class="d-flex align-center justify-space-between">
							<div class="finalsite-sso-title">Finalsite SAML SSO</div>
							<v-btn
								color="transparent"
								icon
								@click="state.ssoModal = false"
								data-cy="sso-modal-close"
							>
								<v-icon>mdi-close</v-icon>
							</v-btn>
						</div>
						<SamlSettings
							v-if="state.ssoModal"
							provider-name="Finalsite"
							default-subscriber-key="finalsite"
						/>
					</div>
				</SideModal>
			</div>
		</div>
	</Layout>
</template>

<script setup lang="ts">
import { reactive } from "vue";
import Layout from "./Layout.vue";
import { useSchoolStore } from "@/stores/school";
import { watch } from "vue";
import ecsTaskSync from "@/components/school/integration/ecsTaskSync.vue";
import csvIntegration from "@/components/school/integration/csvIntegration.vue";
import BlackBaud from "@/components/school/integration/BlackBaud.vue";
import BlackbaudNew from "@/components/school/integration/BlackbaudNew.vue";
import OneRosterSchools from "@/components/school/integration/OneRosterSchools.vue";
import NycDoe2 from "@/components/school/integration/NycDoe2.vue";
import OneRoster from "@/components/school/integration/OneRoster.vue";
import { asyncComputed } from "@/utils/asyncComputed";
import FetchService from "@/services/fetch.service";
import type { OtherIntegration } from "@/api_types/school";
import {
	integrationTypes,
	parseSynergySsoConfig,
	selectableIntegrationTypes,
} from "@/components/school/integration/utils";
import FactsApi from "@/components/school/integration/FactsApi.vue";
import InstructionsModal from "@/components/school/integration/instructionsModal.vue";
import SynergySsoModal from "@/components/school/integration/synergySsoModal.vue";
import SamlSettings from "@/components/school/integration/samlSettings.vue";
import SideModal from "@/components/sideModal.vue";
import { computed } from "vue";
defineProps<{
	account?: string;
}>();

const schoolStore = useSchoolStore();

const state = reactive({
	integration: (schoolStore.schoolDetails.data
		?.integration as any) as typeof integrationTypes[number]["value"],
	ssoModal: false,
	endpoint_url: "",
	private_key: "",
	login_url: "",
	appname: "",
	nycdoe_school_code: "",
	reset_password_required: schoolStore.school?.require_password_reset || false,
	facts_api_key: "",
	facts_subscription_key: "08e35083c8254d22bd3a1a7687d2d4c9",
	instructionsModal: false,
	loadingSave: false,
	csvOnerosterTab: "oneroster" as "csv" | "oneroster",
	synergySsoEnabled: true,
	synergyPxpUrl: "",
	synergyApplicationName: "",
});

watch(
	() => schoolStore.schoolDetails.data,
	(newData) => {
		if (newData) {
			if (!state.integration) {
				state.integration = newData.integration as any;
			}
			// state.nycdoe_school_code = newData.nycdoe_school_code || "";
			// console.log("Updated school code:", state.nycdoe_school_code);
		}
	},
	{ immediate: true } // This ensures the watcher runs immediately with the initial value
);

watch(
	() => state.integration,
	() => {
		state.ssoModal = false;
	}
);
const {
	data: integrationData,
	loading: loadingIntegration,
	reload: reloadIntegration,
} = asyncComputed(
	async (controller) => {
		if (
			![
				"blackbaud",
				"blackbaud_new",
				"sungard",
				"seniorsystems",
				"infinitecampus",
				"synergy",
				"facts",
				"oneroster",
				"nycdoe",
			].includes(state.integration)
		)
			return;
		const integrationEndpoint =
			state.integration === "blackbaud_new" ? "blackbaud" : state.integration;
		const res = await FetchService.fetchData(
			"/integration/" + integrationEndpoint,
			"get",
			undefined,
			undefined,
			controller
		);
		return res;
	},
	[() => state.integration]
);

const showSave = computed(() => {
	if (state.integration === "synergy") {
		const savedSso = parseSynergySsoConfig(integrationData.value);
		if (
			state.synergySsoEnabled !== (integrationData.value?.sso_enabled ?? true) ||
			state.synergyPxpUrl !== savedSso.pxp_url ||
			state.synergyApplicationName !== savedSso.application_name
		) {
			return true;
		}
	}

	// Don't show save button if integration data is still loading
	if (!integrationData.value) {
		return false;
	}

	if (state.integration === "facts" 
	&& (state.facts_api_key !== integrationData.value?.api_key)) {
		return true;
	}
	if (
		state.integration === "infinitecampus" &&
		(state.login_url !== integrationData.value?.login_url ||
			state.appname !== integrationData.value?.appname)
	)
		return true;
	if (
		state.integration === "sungard" &&
		state.private_key !== integrationData.value?.private_key
	)
		return true;
	if (
		state.integration === "seniorsystems" &&
		state.endpoint_url !== integrationData.value?.endpoint_url
	)
		return true;
	if (
		state.integration === "nycdoe" &&
		state.nycdoe_school_code !== integrationData.value?.school_code
	)
		return true;

	return false;
});

watch(integrationData, () => {
	if (
		integrationData.value &&
		state.integration !== "blackbaud" &&
		state.integration !== "blackbaud_new"
	) {
		const d = integrationData.value as OtherIntegration;

		if (state.integration === "sungard") state.private_key = d.private_key || "";
		if (state.integration === "seniorsystems") state.endpoint_url = d.endpoint_url || "";
		if (state.integration === "infinitecampus") {
			state.login_url = d.login_url || "";
			state.appname = d.appname || "";
		}
		if (state.integration === "synergy") {
			state.synergySsoEnabled = d.sso_enabled ?? true;
			const sso = parseSynergySsoConfig(d);
			state.synergyPxpUrl = sso.pxp_url;
			state.synergyApplicationName = sso.application_name;
		}

		if (state.integration === "facts") {
			state.facts_api_key = d.api_key;
			// state.facts_subscription_key = d.subscription_key;
		}

		if (state.integration === "nycdoe") {
			state.nycdoe_school_code = d.school_code || "";
		}
	}
});

const save = async () => {
	state.loadingSave = true;

	let payload: any = {};
	if (state.integration === "blackbaud" || state.integration === "blackbaud_new") {
		payload = {};
	}
	if (state.integration === "sungard") {
		payload = {
			private_key: state.private_key,
		};
	}
	if (state.integration === "seniorsystems") {
		payload = {
			endpoint_url: state.endpoint_url,
		};
	}
	if (state.integration === "infinitecampus") {
		payload = {
			login_url: state.login_url,
			appname: state.appname,
			portal_url: "none",
			district_id: 0,
		};
	}
	if (state.integration === "synergy") {
		payload = {
			sso_enabled: state.synergySsoEnabled,
			pxp_url: state.synergyPxpUrl,
			application_name: state.synergyApplicationName,
		};
	}
	if (state.integration === "facts") {
		payload = {
			subscription_key: state.facts_subscription_key,
			api_key: state.facts_api_key,
		};
	}
	if (state.integration === "nycdoe") {
		payload = {
			school_code: state.nycdoe_school_code,
		};
	}

	payload["require_password_reset"] = state.reset_password_required;

	const res = await FetchService.fetchData(
		"/integration/" + state.integration,
		"put",
		payload
	);
	schoolStore.schoolDetails.data.integration = state.integration;
	// blackbaud_new shares OAuth/connection state with /integration/blackbaud;
	// do not overwrite that status with the school-type save response.
	if (state.integration === "blackbaud_new") {
		await reloadIntegration();
	} else if (typeof res === "object") {
		integrationData.value = res;
	}
	state.loadingSave = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.integration-top {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	margin-top: 20px;
	.title {
		font-size: 2rem;
		color: rgb(46, 46, 46);
		font-weight: 600;
	}

	.actions {
		display: flex;
		justify-content: flex-end;
		//   width: 100%;
		gap: 20px;
		flex-grow: 1;
		.action {
			color: $primary;
			display: flex;
			flex-direction: column;
			align-items: center;
			cursor: pointer;
			padding: 5px;
			border-radius: 4px;

			&:active {
				outline: 2px solid $primary-75;
			}
			gap: 5px;
			.icon {
				font-size: 1.5rem;
				font-weight: 600;
				flex-grow: 1;
			}
			.text {
				font-weight: 600;
				text-transform: uppercase;
			}
		}
	}
}
.integration-body {
	margin-top: 30px;
	.title {
		color: $primary;
		font-weight: 600;
		font-size: 1.25rem;
	}
}

.finalsite-sso-modal {
	padding: 20px 40px;
}

.finalsite-sso-title {
	color: $primary;
	font-size: 1.25rem;
	font-weight: 600;
}

.scrollable-table-container {
	height: 50vh;
	max-height: 50vh;
	overflow-y: auto;
	margin-top: 16px;
	background: white;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
</style>
